<template>
    <div class="mb-5 pb-5">
        <div class="rounded-xl px-6 py-5 mb-5 flex items-center gap-4">
            <user-avatar
                size="xlarge"
                :picture-url="user.picture"
                :name="itemComponent.firstName"
            />
            <div>
                <h1 class="text-2xl font-bold leading-tight">
                    {{ itemComponent.firstName || "Usuario" }}
                    {{ itemComponent.lastName || "" }}
                </h1>
                <p v-if="itemComponent.email" class="text-sm opacity-80 mt-0.5">
                    {{ itemComponent.email }}
                </p>
            </div>
        </div>
        <div class="flex flex-col md:flex-row md:justify-between mt-5">
            <h1 class="text-3xl text-center md:text-left">
                Perfil de
                <strong class="text-theme-primary">
                    {{ itemComponent.firstName || "usuario" }}
                </strong>
            </h1>
            <div class="flex items-center justify-center mt-2 md:mt-0 gap-2">
                <app-button
                    icon
                    circle
                    color="brand-purple"
                    @click="reloadItem(true)"
                >
                    <reload-icon class="h-5 w-5 text-white" />
                </app-button>
                <custom-switch v-model="enabledUpdate">
                    <span class="text-theme-inverse">🖊</span>
                </custom-switch>
            </div>
        </div>

        <div class="my-3 flex">
            <custom-navbar
                id="tabs-profile"
                v-model="activeTab"
                center-collapsed
                not-line
            >
                <template #left>
                    <custom-navbar-item
                        id="info"
                        :active="activeTab === 'info'"
                        class="px-2 md:px-4 xl:px-6"
                    >
                        <div class="flex">
                            <span class="lg:block hidden"> Datos </span>
                            <span>&nbsp;👤</span>
                        </div>
                    </custom-navbar-item>
                    <custom-navbar-item
                        v-if="merchantsOwn.length"
                        id="merchants"
                        :active="activeTab === 'merchants'"
                        class="px-2 md:px-4 xl:px-6"
                    >
                        <div class="flex">
                            <span class="lg:block hidden"> Comercios </span>
                            <span>&nbsp;🏪</span>
                        </div>
                    </custom-navbar-item>
                    <custom-navbar-item
                        id="roles"
                        :active="activeTab === 'roles'"
                        class="px-2 md:px-4 xl:px-6"
                    >
                        <div class="flex">
                            <span class="lg:block hidden"> Roles </span>
                            <span>&nbsp;🎭</span>
                        </div>
                    </custom-navbar-item>
                </template>
            </custom-navbar>
        </div>

        <div v-show="activeTab === 'info'">
            <div class="rounded-xl p-4 mt-2">
                <p
                    class="text-xs text-theme-muted uppercase tracking-widest mb-4"
                >
                    🔑 Acceso
                </p>
                <div
                    class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4 mt-6"
                >
                    <custom-input
                        v-model="itemComponent.id"
                        class="w-full"
                        label="Id"
                        disabled
                    />
                    <custom-input
                        v-model="itemComponent.email"
                        class="w-full"
                        label="Email"
                        disabled
                    />
                    <custom-input
                        v-if="hasAdminRole"
                        :value="tenantId"
                        class="w-full"
                        label="Tenant Id"
                        disabled
                    />
                </div>
            </div>

            <div class="rounded-xl p-4 mt-2">
                <p
                    class="text-xs text-theme-muted uppercase tracking-widest mb-4"
                >
                    📋 Datos personales
                </p>
                <div
                    class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4 mt-6"
                >
                    <custom-input
                        v-model="itemComponent.firstName"
                        class="w-full"
                        label="Nombres"
                        :disabled="!enabledUpdate"
                    />
                    <custom-input
                        v-model="itemComponent.lastName"
                        label="Apellidos"
                        class="w-full"
                        :disabled="!enabledUpdate"
                    />
                    <identification-type-selector
                        v-model="itemComponent.identificationType"
                        :enabled="enabledUpdate"
                    />
                    <custom-input
                        v-model="itemComponent.identificationNumber"
                        label="Número de Identificación"
                        class="w-full"
                        :disabled="!enabledUpdate"
                    />
                    <custom-input
                        v-model="itemComponent.phone"
                        label="Celular de contacto"
                        class="w-full"
                        type="number"
                        :disabled="!enabledUpdate"
                    />
                    <city-code-selector
                        v-model="itemComponent.cityCode"
                        :enabled="enabledUpdate"
                    />
                    <custom-input
                        v-model="itemComponent.address"
                        label="Dirección"
                        class="w-full"
                        :disabled="!enabledUpdate"
                    />
                    <custom-input
                        v-model="itemComponent.addressComplement"
                        label="Complemento de dirección"
                        class="w-full"
                        :disabled="!enabledUpdate"
                    />
                    <custom-input
                        v-model="itemComponent.neighborhood"
                        label="Barrio"
                        class="w-full"
                        :disabled="!enabledUpdate"
                    />
                </div>
            </div>

            <div v-if="enabledUpdate" class="flex justify-center my-4">
                <app-button @click="updateBasicDataUser">
                    Actualizar información
                </app-button>
            </div>
        </div>

        <div v-show="activeTab === 'merchants'">
            <h2 class="text-theme-primary text-2xl">Tiendas con acceso 🏪</h2>
            <div
                v-if="merchantsOwn.length"
                class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4"
            >
                <merchant-access-card
                    v-for="item in merchantsOwn"
                    :key="item.id"
                    :merchant="item"
                />
            </div>
            <div v-else class="flex justify-center mt-8">
                <span class="text-theme-muted text-lg">
                    Sin tiendas asignados.
                </span>
            </div>
        </div>

        <div v-show="activeTab === 'roles'">
            <h2 class="text-theme-primary text-2xl">Roles 🎭</h2>
            <div class="mt-4">
                <div
                    v-for="(item, index) in roles"
                    :key="index"
                    class="flex items-center my-2 p-3 rounded-lg border border-stack-primary"
                >
                    <custom-avatar
                        color="#d3d4f9"
                        :badge="index === 0"
                        badge-color="danger"
                    >
                        {{ $data.$rolesEmoji[item.slug] || "👷‍♂️" }}
                    </custom-avatar>
                    <strong class="ml-3 capitalize text-theme-base">
                        {{ item.name }}
                    </strong>
                </div>
                <div v-if="!roles.length" class="flex justify-center mt-8">
                    <span class="text-theme-muted text-lg">
                        Sin roles asignados.
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import tabQuerySyncMixin from "@/mixins/tabQuerySyncMixin";
import { mapActions, mapGetters, mapState } from "vuex";

import { rolesEmoji } from "@/common/fields/roles";

import ReloadIcon from "@/components/icons/ReloadIcon.vue";
import IdentificationTypeSelector from "@/components/users/IdentificationTypeSelector.vue";
import CityCodeSelector from "@/components/utils/CityCodeSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import MerchantAccessCard from "@/components/utils/MerchantAccessCard.vue";
import UserAvatar from "@/components/utils/UserAvatar.vue";

export default {
    name: "ProfilePage",
    components: {
        IdentificationTypeSelector,
        CityCodeSelector,
        ReloadIcon,
        MerchantAccessCard,
        CustomInput,
        UserAvatar
    },
    mixins: [tabQuerySyncMixin],
    data: () => ({
        activeTab: "info",
        $tabsWhitelist: ["info", "merchants", "roles"],
        enabledUpdate: false,
        itemComponent: {
            identificationType: "cc",
            identificationNumber: "",
            username: "",
            firstName: "",
            lastName: "",
            cityCode: "11001",
            address: "",
            neighborhood: "",
            email: "",
            phone: ""
        },
        introValidators: {
            phone: false,
            identificationNumber: false,
            firstName: false,
            lastName: false
        },
        isLoading: true,
        $rolesEmoji: rolesEmoji
    }),
    computed: {
        ...mapState("auth", ["user", "roles", "hasAdminRole", "tenantId"]),
        ...mapState("commons", ["merchantsOwn"]),
        ...mapGetters("control", ["backgroundColor"])
    },
    async mounted() {
        this.itemComponent = { ...this.user };
        if (!this.itemComponent.identificationNumber) {
            this.reloadItem(true);
        }
    },
    methods: {
        ...mapActions("auth", ["getProfile", "updateProfile"]),
        async updateBasicDataUser() {
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateProfile(this.itemComponent);
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async reloadItem(complete = false) {
            this.isLoading = true;
            this.$loading.show();
            try {
                this.itemComponent = await this.getProfile({ complete });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
